import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { OfferType } from 'services/admin/platformAdminOffer.types';
import { usePlatformAdminOffersApi } from 'services/admin/usePlatformAdminOffersApi';

export const PLATFORM_ADMIN_OFFERS_QUERY_KEY = 'platform-admin-offers';

export type PlatformAdminOfferTypeFilter = 'all' | OfferType;

export type PlatformAdminOfferStatusFilter = 'all' | 'draft' | 'published';

export type PlatformAdminOfferAvailabilityFilter =
  | 'active'
  | 'suspended'
  | 'removed'
  | 'all';

export type PlatformAdminOfferSortOption =
  | 'name-asc'
  | 'name-desc'
  | 'publishedAt-desc'
  | 'publishedAt-asc';

const resolveVisible = (statusFilter: PlatformAdminOfferStatusFilter) => {
  if (statusFilter === 'published') {
    return true;
  }
  if (statusFilter === 'draft') {
    return false;
  }
  return undefined;
};

const resolveSort = (sortOption: PlatformAdminOfferSortOption) => {
  if (sortOption === 'name-desc') {
    return { sortBy: 'name', sortDirection: 'desc' } as const;
  }
  if (sortOption === 'publishedAt-desc') {
    return { sortBy: 'publishedAt', sortDirection: 'desc' } as const;
  }
  if (sortOption === 'publishedAt-asc') {
    return { sortBy: 'publishedAt', sortDirection: 'asc' } as const;
  }
  return { sortBy: 'name', sortDirection: 'asc' } as const;
};

export const useSearchPlatformAdminOffers = (
  search: string,
  page: number,
  size: number,
  typeFilter: PlatformAdminOfferTypeFilter = 'all',
  statusFilter: PlatformAdminOfferStatusFilter = 'published',
  availabilityFilter: PlatformAdminOfferAvailabilityFilter = 'all',
  sortOption: PlatformAdminOfferSortOption = 'name-asc'
) => {
  const { searchOffers } = usePlatformAdminOffersApi();
  const roomType = typeFilter === 'all' ? undefined : typeFilter;
  const visible = resolveVisible(statusFilter);
  const { sortBy, sortDirection } = resolveSort(sortOption);

  return useQuery({
    queryKey: [
      PLATFORM_ADMIN_OFFERS_QUERY_KEY,
      search,
      page,
      size,
      typeFilter,
      statusFilter,
      availabilityFilter,
      sortOption
    ],
    queryFn: () =>
      searchOffers(
        search || undefined,
        page,
        size,
        roomType,
        visible,
        availabilityFilter,
        sortBy,
        sortDirection
      ),
    placeholderData: keepPreviousData
  });
};
